/**
 * Inline Overview tile editor — the per-app KPI tiles are configured
 * directly on the Overview page (not only in App Settings). Each
 * non-pinned tile reveals a pencil (top-right) to pick its event +
 * metric, and a trash (top-left) to remove it; empty slots show a "+"
 * to add one. A single Save bar at the bottom commits the whole tile
 * mix via the same `/api/app-settings/overview-tiles` PUT the Settings
 * editor uses (§36). Spend + Installs stay pinned + read-only forever.
 *
 * Values shown on each tile come from the resolved `tile_config` on the
 * /api/stats/overview response; a freshly-picked (unsaved) tile shows
 * "—" until Save, at which point the overview refetch fills it in.
 */

import {
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useLayer } from "@/lib/layer-stack";
import { createPortal } from "react-dom";
import { Btn } from "@/components/Btn";
import { StatusToast } from "@/components/StatusToast";
import { ConfigurableKpi } from "@/components/overview/ConfigurableKpi";
import { PerAppTile } from "@/components/overview/PerAppTile";
import {
  useOverviewTilesConfig,
  useUpsertOverviewTilesConfig,
} from "@/lib/api-hooks";
import type {
  AvailableEvent,
  DailyPoint,
  EventSource,
  MetricKind,
  OverviewKpis,
  OverviewTileSlot,
  OverviewTileValue,
} from "@/lib/types";

const MAX_SLOTS = 6;
const EVENT_SOURCES: EventSource[] = ["asa", "adapty", "revenuecat", "adjust"];
const SOURCE_LABEL: Record<EventSource, string> = {
  asa: "ASA performance",
  adapty: "Adapty",
  revenuecat: "RevenueCat",
  adjust: "Adjust",
};

type DraftSlot = {
  slot_index: number;
  event_type: string;
  event_source: EventSource | "";
  metric_kind: MetricKind;
  label: string;
  denominator_event_type: string;
  denominator_event_source: EventSource | "";
};

function emptyDraft(slot_index: number): DraftSlot {
  return {
    slot_index,
    event_type: "",
    event_source: "",
    metric_kind: "count",
    label: "",
    denominator_event_type: "",
    denominator_event_source: "",
  };
}

function tilesToDrafts(tiles: OverviewTileSlot[]): DraftSlot[] {
  const out: DraftSlot[] = [];
  for (let i = 0; i < MAX_SLOTS; i += 1) {
    const e = tiles.find((t) => t.slot_index === i);
    out.push(
      e
        ? {
            slot_index: i,
            event_type: e.event_type,
            event_source: e.event_source,
            metric_kind: e.metric_kind,
            label: e.label ?? "",
            denominator_event_type: e.denominator_event_type ?? "",
            denominator_event_source: e.denominator_event_source ?? "",
          }
        : emptyDraft(i),
    );
  }
  return out;
}

function draftsToTiles(drafts: DraftSlot[]): OverviewTileSlot[] {
  return drafts
    .filter((d) => d.event_type && d.event_source)
    .filter(
      (d) =>
        d.metric_kind !== "conversion" ||
        (d.denominator_event_type && d.denominator_event_source),
    )
    .map((d) => ({
      slot_index: d.slot_index,
      event_type: d.event_type,
      event_source: d.event_source as EventSource,
      metric_kind: d.metric_kind,
      label: d.label.trim() ? d.label.trim() : null,
      denominator_event_type:
        d.metric_kind === "conversion" ? d.denominator_event_type : null,
      denominator_event_source:
        d.metric_kind === "conversion"
          ? (d.denominator_event_source as EventSource)
          : null,
    }));
}

function groupEvents(
  events: AvailableEvent[],
): Record<EventSource, AvailableEvent[]> {
  const out: Record<EventSource, AvailableEvent[]> = {
    adapty: [],
    revenuecat: [],
    adjust: [],
    asa: [],
  };
  for (const ev of events) out[ev.event_source].push(ev);
  for (const k of Object.keys(out) as EventSource[]) {
    out[k].sort((a, b) => b.count_30d - a.count_30d);
  }
  return out;
}

function fmtThousands(n: number): string {
  return n.toLocaleString("en-US");
}
function encodeOption(src: EventSource, evType: string): string {
  return `${src}|${evType}`;
}
function decodeOption(
  v: string,
): { source: EventSource; event_type: string } | null {
  const idx = v.indexOf("|");
  if (idx < 0) return null;
  const src = v.slice(0, idx) as EventSource;
  const evType = v.slice(idx + 1);
  if (!evType) return null;
  return { source: src, event_type: evType };
}

/** Canonical signature of a tile set for dirty-checking. */
function canon(pin: boolean, tiles: OverviewTileSlot[]): string {
  const sorted = [...tiles].sort((a, b) => a.slot_index - b.slot_index);
  return JSON.stringify({
    pin,
    tiles: sorted.map((t) => ({
      i: t.slot_index,
      e: t.event_type,
      s: t.event_source,
      m: t.metric_kind,
      l: t.label ?? "",
      dt: t.denominator_event_type ?? "",
      ds: t.denominator_event_source ?? "",
    })),
  });
}

function sameIdentity(d: DraftSlot, r: OverviewTileValue): boolean {
  return (
    d.event_type === r.event_type &&
    d.event_source === r.event_source &&
    d.metric_kind === r.metric_kind &&
    (d.denominator_event_type || "") === (r.denominator_event_type ?? "") &&
    (d.denominator_event_source || "") === (r.denominator_event_source ?? "")
  );
}

// Popover positioning — anchored under a trigger, kept inside the
// viewport, rendered through a portal so the tile's overflow:hidden
// never clips it. (Same approach as MetricPicker.)
function usePopoverCoords(
  ref: React.RefObject<HTMLElement>,
  open: boolean,
  desiredWidth: number,
): { top: number; left: number; width: number } | null {
  const [coords, setCoords] = useState<{
    top: number;
    left: number;
    width: number;
  } | null>(null);
  useLayoutEffect(() => {
    if (!open || !ref.current) {
      setCoords(null);
      return;
    }
    const update = () => {
      const r = ref.current!.getBoundingClientRect();
      const width = Math.max(desiredWidth, r.width);
      let left = r.left;
      if (left + width > window.innerWidth - 8) {
        left = window.innerWidth - 8 - width;
      }
      if (left < 8) left = 8;
      setCoords({ top: r.bottom + 6, left, width });
    };
    update();
    window.addEventListener("scroll", update, true);
    window.addEventListener("resize", update);
    return () => {
      window.removeEventListener("scroll", update, true);
      window.removeEventListener("resize", update);
    };
  }, [open, desiredWidth, ref]);
  return coords;
}

// --- the per-slot picker popover -------------------------------------------

function SlotPickerPopover({
  anchorRef,
  draft,
  grouped,
  noEvents,
  onChange,
  onClose,
}: {
  anchorRef: React.RefObject<HTMLElement>;
  draft: DraftSlot;
  grouped: Record<EventSource, AvailableEvent[]>;
  noEvents: boolean;
  onChange: (patch: Partial<DraftSlot>) => void;
  onClose: () => void;
}) {
  const popRef = useRef<HTMLDivElement>(null);
  const coords = usePopoverCoords(anchorRef, true, 320);

  useLayer(true, onClose);
  useEffect(() => {
    const h = (e: MouseEvent) => {
      const t = e.target as Node;
      if (
        anchorRef.current &&
        !anchorRef.current.contains(t) &&
        popRef.current &&
        !popRef.current.contains(t)
      ) {
        onClose();
      }
    };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, [anchorRef, onClose]);

  if (!coords) return null;

  const selectedKey = draft.event_source
    ? encodeOption(draft.event_source as EventSource, draft.event_type)
    : "";

  return createPortal(
    <div
      ref={popRef}
      style={{
        position: "fixed",
        top: coords.top,
        left: coords.left,
        width: coords.width,
        zIndex: 500,
        background: "var(--c-surface)",
        border: "1px solid var(--c-border)",
        borderRadius: 10,
        boxShadow: "var(--sh-3)",
        padding: 12,
        display: "flex",
        flexDirection: "column",
        gap: 10,
      }}
    >
      {noEvents ? (
        <div style={{ fontSize: 12.5, lineHeight: 1.5 }} className="muted">
          No events observed for this app in the last 30 days. ASA
          performance metrics still appear once spend lands.
        </div>
      ) : null}

      <div>
        <div style={pLabel}>Event / metric</div>
        <select
          className="select"
          value={selectedKey}
          onChange={(e) => {
            const d = decodeOption(e.target.value);
            if (!d) {
              onChange({ event_source: "", event_type: "" });
              return;
            }
            // Picking an ASA performance metric drops the metric_kind
            // (single-value formula) and clears any conversion denom.
            const patch: Partial<DraftSlot> = {
              event_source: d.source,
              event_type: d.event_type,
            };
            if (d.source === "asa") {
              patch.metric_kind = "count";
              patch.denominator_event_type = "";
              patch.denominator_event_source = "";
            }
            onChange(patch);
          }}
          style={{ width: "100%", minWidth: 0 }}
        >
          <option value="">— pick event —</option>
          {EVENT_SOURCES.map((src) => {
            const items = grouped[src];
            if (!items || items.length === 0) return null;
            return (
              <optgroup key={src} label={SOURCE_LABEL[src]}>
                {items.map((ev) => (
                  <option
                    key={`${src}|${ev.event_type}`}
                    value={encodeOption(src, ev.event_type)}
                  >
                    {ev.label
                      ? ev.label
                      : `${ev.event_type} (${fmtThousands(ev.count_30d)})`}
                  </option>
                ))}
              </optgroup>
            );
          })}
        </select>
      </div>

      {/* metric_kind — hidden for ASA performance metrics (fixed formula) */}
      {draft.event_source && draft.event_source !== "asa" && (
        <div>
          <div style={pLabel}>Metric</div>
          <div
            style={{
              display: "inline-flex",
              border: "1px solid var(--c-border)",
              borderRadius: 8,
              overflow: "hidden",
            }}
          >
            {(["count", "revenue", "cpa", "conversion"] as MetricKind[]).map(
              (mk) => {
                const on = draft.metric_kind === mk;
                return (
                  <button
                    key={mk}
                    type="button"
                    aria-pressed={on}
                    onClick={() => onChange({ metric_kind: mk })}
                    style={{
                      padding: "6px 11px",
                      fontSize: 12,
                      fontWeight: on ? 600 : 500,
                      background: on ? "var(--c-accent-600)" : "transparent",
                      color: on ? "#fff" : "var(--c-text-2)",
                      border: "none",
                      borderRight: "1px solid var(--c-border)",
                      cursor: "pointer",
                    }}
                  >
                    {mk === "count"
                      ? "Count"
                      : mk === "revenue"
                        ? "Revenue"
                        : mk === "cpa"
                          ? "CPA"
                          : "CR"}
                  </button>
                );
              },
            )}
          </div>
        </div>
      )}

      {/* conversion denominator */}
      {draft.metric_kind === "conversion" && draft.event_source !== "asa" && (
        <div>
          <div style={pLabel}>Conversion denominator</div>
          <select
            className="select"
            value={
              draft.denominator_event_source && draft.denominator_event_type
                ? encodeOption(
                    draft.denominator_event_source as EventSource,
                    draft.denominator_event_type,
                  )
                : ""
            }
            onChange={(e) => {
              const d = decodeOption(e.target.value);
              onChange({
                denominator_event_source: d ? d.source : "",
                denominator_event_type: d ? d.event_type : "",
              });
            }}
            style={{ width: "100%", minWidth: 0 }}
          >
            <option value="">— pick denominator —</option>
            {EVENT_SOURCES.map((src) => {
              const items = grouped[src];
              if (!items || items.length === 0) return null;
              return (
                <optgroup key={src} label={SOURCE_LABEL[src]}>
                  {items
                    .filter(
                      (ev) =>
                        src !== "asa" ||
                        ev.event_type === "installs" ||
                        ev.event_type === "taps" ||
                        ev.event_type === "impressions",
                    )
                    .map((ev) => (
                      <option
                        key={`${src}|${ev.event_type}`}
                        value={encodeOption(src, ev.event_type)}
                      >
                        {ev.label
                          ? ev.label
                          : `${ev.event_type} (${fmtThousands(ev.count_30d)})`}
                      </option>
                    ))}
                </optgroup>
              );
            })}
          </select>
        </div>
      )}

      <div>
        <div style={pLabel}>Label (optional)</div>
        <input
          type="text"
          className="input"
          placeholder="Custom label"
          value={draft.label}
          maxLength={80}
          onChange={(e) => onChange({ label: e.target.value })}
          style={{ width: "100%", fontSize: 13 }}
        />
      </div>

      <div style={{ display: "flex", justifyContent: "flex-end" }}>
        <Btn variant="ghost" onClick={onClose}>
          Done
        </Btn>
      </div>
    </div>,
    document.body,
  );
}

const pLabel: React.CSSProperties = {
  fontSize: 10.5,
  fontWeight: 600,
  letterSpacing: 0.4,
  color: "var(--c-text-4)",
  textTransform: "uppercase",
  marginBottom: 5,
};

// --- a single editable slot (empty "+" OR a filled tile) -------------------

function SlotTile({
  draft,
  resolved,
  grouped,
  noEvents,
  onChange,
  onClear,
}: {
  draft: DraftSlot;
  resolved: OverviewTileValue | null;
  grouped: Record<EventSource, AvailableEvent[]>;
  noEvents: boolean;
  onChange: (patch: Partial<DraftSlot>) => void;
  onClear: () => void;
}) {
  const [open, setOpen] = useState(false);
  const [confirming, setConfirming] = useState(false);
  useLayer(confirming, () => setConfirming(false));
  const rootRef = useRef<HTMLDivElement>(null);
  const pencilRef = useRef<HTMLButtonElement>(null);

  const filled = !!(draft.event_type && draft.event_source);

  // Empty slot → dashed "+" placeholder.
  if (!filled) {
    return (
      <>
        <div
          ref={rootRef}
          className="kpi kpi-empty hover-reveal"
          role="button"
          tabIndex={0}
          onClick={() => setOpen(true)}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " ") setOpen(true);
          }}
        >
          <span className="kpi-empty-plus">+</span>
        </div>
        {open && (
          <SlotPickerPopover
            anchorRef={rootRef}
            draft={draft}
            grouped={grouped}
            noEvents={noEvents}
            onChange={onChange}
            onClose={() => setOpen(false)}
          />
        )}
      </>
    );
  }

  const matches = resolved ? sameIdentity(draft, resolved) : false;
  const synth: OverviewTileValue = {
    slot_index: draft.slot_index,
    event_type: draft.event_type,
    event_source: draft.event_source as EventSource,
    metric_kind: draft.metric_kind,
    label: draft.label ? draft.label : null,
    value: matches ? resolved!.value : null,
    delta_pct: matches ? resolved!.delta_pct : null,
    denominator_event_type: draft.denominator_event_type || null,
    denominator_event_source:
      (draft.denominator_event_source || null) as EventSource | null,
  };

  const overlay = (
    <>
      <button
        type="button"
        className="hover-reveal-btn hover-reveal-btn--left"
        aria-label="Remove tile"
        title="Remove tile"
        onClick={() => setConfirming(true)}
        style={trashBtnStyle}
      >
        <svg width="12" height="12" viewBox="0 0 16 16" fill="none">
          <path
            d="M3 4.5h10M6.5 4V3h3v1M5 4.5l.5 8.5h5l.5-8.5"
            stroke="currentColor"
            strokeWidth="1.3"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </button>
      <button
        ref={pencilRef}
        type="button"
        className="hover-reveal-btn"
        aria-label="Configure tile"
        title="Configure tile"
        onClick={() => setOpen(true)}
        style={pencilBtnStyle(open)}
      >
        <svg width="12" height="12" viewBox="0 0 16 16" fill="none">
          <path
            d="M11.5 2.5l2 2L5 13H3v-2l8.5-8.5z"
            stroke="currentColor"
            strokeWidth="1.3"
            strokeLinejoin="round"
          />
        </svg>
      </button>
      {confirming && (
        <div className="tile-confirm-overlay">
          <div style={{ fontSize: 13, fontWeight: 500 }}>Remove this tile?</div>
          <div style={{ display: "flex", gap: 8 }}>
            <Btn variant="ghost" onClick={() => setConfirming(false)}>
              Cancel
            </Btn>
            <Btn
              variant="danger"
              onClick={() => {
                setConfirming(false);
                onClear();
              }}
            >
              Remove
            </Btn>
          </div>
        </div>
      )}
    </>
  );

  return (
    <>
      <PerAppTile tile={synth} className="hover-reveal" overlay={overlay} />
      {open && (
        <SlotPickerPopover
          anchorRef={pencilRef}
          draft={draft}
          grouped={grouped}
          noEvents={noEvents}
          onChange={onChange}
          onClose={() => setOpen(false)}
        />
      )}
    </>
  );
}

const trashBtnStyle: React.CSSProperties = {
  width: 22,
  height: 22,
  borderRadius: 6,
  border: "1px solid var(--c-border)",
  background: "var(--c-surface)",
  color: "var(--c-text-3)",
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  cursor: "pointer",
  padding: 0,
};
function pencilBtnStyle(active: boolean): React.CSSProperties {
  return {
    ...trashBtnStyle,
    background: active ? "var(--c-accent-50)" : "var(--c-surface)",
    color: active ? "var(--c-accent-700)" : "var(--c-text-3)",
  };
}

// --- the orchestrator -------------------------------------------------------

export function OverviewTilesInline({
  appId,
  resolvedTiles,
  kpis,
  comparison,
  series,
}: {
  appId: number;
  resolvedTiles: OverviewTileValue[];
  kpis: OverviewKpis;
  comparison: OverviewKpis | null;
  series: DailyPoint[];
}) {
  const cfgQuery = useOverviewTilesConfig(appId);
  const upsert = useUpsertOverviewTilesConfig();
  const [drafts, setDrafts] = useState<DraftSlot[]>(() => tilesToDrafts([]));
  const [pin, setPin] = useState(true);
  const [toast, setToast] = useState<{
    msg: string;
    variant: "success" | "error";
  } | null>(null);

  // Re-seed from the saved config on first load, app switch, and after
  // a successful Save (the upsert invalidates this query). An unrelated
  // overview refetch leaves cfg's reference intact → no re-seed of an
  // in-progress draft.
  const cfg = cfgQuery.data;
  useEffect(() => {
    if (!cfg) return;
    setPin(cfg.pin_spend_installs);
    setDrafts(tilesToDrafts(cfg.tiles));
  }, [cfg]);

  useEffect(() => {
    if (!toast) return;
    const t = setTimeout(() => setToast(null), 2500);
    return () => clearTimeout(t);
  }, [toast]);

  const grouped = useMemo(
    () => groupEvents(cfg?.available_events ?? []),
    [cfg?.available_events],
  );
  const noEvents = (cfg?.available_events?.length ?? 0) === 0;

  const resolvedBySlot = useMemo(() => {
    const m = new Map<number, OverviewTileValue>();
    for (const t of resolvedTiles) m.set(t.slot_index, t);
    return m;
  }, [resolvedTiles]);

  const dirty = useMemo(() => {
    if (!cfg) return false;
    return (
      canon(cfg.pin_spend_installs, cfg.tiles) !==
      canon(pin, draftsToTiles(drafts))
    );
  }, [cfg, pin, drafts]);

  const visibleCount = pin ? 4 : MAX_SLOTS;

  const updateDraft = (i: number, patch: Partial<DraftSlot>) =>
    setDrafts((prev) => prev.map((d, j) => (j === i ? { ...d, ...patch } : d)));
  const clearSlot = (i: number) =>
    setDrafts((prev) =>
      prev.map((d, j) => (j === i ? emptyDraft(d.slot_index) : d)),
    );

  const onSave = async () => {
    try {
      await upsert.mutateAsync({
        app_id: appId,
        body: { pin_spend_installs: pin, tiles: draftsToTiles(drafts) },
      });
      setToast({ msg: "Saved", variant: "success" });
    } catch (e) {
      setToast({
        msg: `Save failed: ${(e as Error).message}`,
        variant: "error",
      });
    }
  };

  const onDiscard = () => {
    if (!cfg) return;
    setPin(cfg.pin_spend_installs);
    setDrafts(tilesToDrafts(cfg.tiles));
  };

  return (
    <>
      {toast && <StatusToast message={toast.msg} variant={toast.variant} />}
      <div className="kpi-grid">
        {pin && (
          <>
            <ConfigurableKpi
              key="pin-spend"
              readOnly
              metricId="spend"
              kpis={kpis}
              comparison={comparison}
              series={series}
            />
            <ConfigurableKpi
              key="pin-installs"
              readOnly
              metricId="installs"
              kpis={kpis}
              comparison={comparison}
              series={series}
            />
          </>
        )}
        {Array.from({ length: visibleCount }).map((_, i) => (
          <SlotTile
            key={drafts[i]?.slot_index ?? i}
            draft={drafts[i] ?? emptyDraft(i)}
            resolved={resolvedBySlot.get(i) ?? null}
            grouped={grouped}
            noEvents={noEvents}
            onChange={(patch) => updateDraft(i, patch)}
            onClear={() => clearSlot(i)}
          />
        ))}
      </div>

      <div className="tile-save-bar">
        <label
          style={{
            display: "inline-flex",
            gap: 7,
            alignItems: "center",
            fontSize: 12.5,
            cursor: "pointer",
            color: "var(--c-text-2)",
          }}
        >
          <input
            type="checkbox"
            checked={pin}
            onChange={(e) => setPin(e.target.checked)}
          />
          Pin Spend + Installs
        </label>
        {dirty && (
          <>
            <Btn
              variant="primary"
              disabled={upsert.isPending}
              onClick={() => void onSave()}
            >
              {upsert.isPending ? "Saving…" : "Save"}
            </Btn>
            <Btn variant="ghost" onClick={onDiscard} disabled={upsert.isPending}>
              Discard
            </Btn>
            <span className="muted" style={{ fontSize: 12 }}>
              Unsaved changes
            </span>
          </>
        )}
      </div>
    </>
  );
}
